import { Modal as AntdModal } from 'antd';
import type { ReactNode } from 'react';

import type { ButtonVariant } from '@/components/common/button';

import { OverlayChrome, OverlayFooter } from '../overlay';

import styles from './modal.module.scss';

export type ModalSize = 'sm' | 'md' | 'lg';

const SIZE_WIDTH: Record<ModalSize, number> = { sm: 464, md: 560, lg: 709 };

export type ModalProps = {
  open: boolean;
  onClose: () => void;
  title?: ReactNode;
  /** Optional supporting line under the title, inside the header band. */
  subtitle?: ReactNode;
  size?: ModalSize;
  width?: number | string;
  maskClosable?: boolean;
  closeOnEsc?: boolean;
  loading?: boolean;
  children: ReactNode;
  primaryText?: string;
  onPrimary?: () => void;
  primaryVariant?: ButtonVariant;
  primaryDanger?: boolean;
  /**
   * Disables the primary/submit button without putting it in a loading state. Distinct from
   * `loading`: a form whose mandatory fields are not yet valid must render Save *disabled* rather
   * than busy (US-117851 Rule 25 is the first case that needed it).
   */
  primaryDisabled?: boolean;
  /** id of the element explaining why the primary action is unavailable. */
  primaryDescribedBy?: string;
  /** Content at the start of the footer row, opposite the buttons. */
  footerStart?: ReactNode;
  cancelText?: string;
  onCancel?: () => void;
  /** Internal: used by FormModal to wrap body + footer in a <form>. */
  onSubmit?: React.ComponentProps<'form'>['onSubmit'];
  /** Internal: makes the primary footer button type="submit". */
  primaryIsSubmit?: boolean;
  footerAlign?: 'end' | 'center' | 'fill';
  hideFooter?: boolean;
  /** Extra class on the body region — forwarded to `<OverlayChrome>`. */
  bodyClassName?: string;
};

export function Modal({
  open,
  onClose,
  title,
  subtitle,
  size = 'md',
  width,
  maskClosable = true,
  closeOnEsc = true,
  loading = false,
  children,
  primaryText,
  onPrimary,
  primaryVariant = 'neutral',
  primaryDanger,
  primaryDisabled = false,
  primaryDescribedBy,
  footerStart,
  cancelText = 'Cancel',
  onCancel,
  onSubmit,
  primaryIsSubmit = false,
  footerAlign = 'end',
  hideFooter = false,
  bodyClassName,
}: ModalProps) {
  const footer = hideFooter ? undefined : (
    <OverlayFooter
      primaryText={primaryText}
      onPrimary={onPrimary}
      isSubmit={primaryIsSubmit}
      primaryVariant={primaryVariant}
      primaryDanger={primaryDanger}
      loading={loading}
      disabled={primaryDisabled}
      describedBy={primaryDescribedBy}
      start={footerStart}
      cancelText={cancelText}
      onCancel={onCancel ?? onClose}
      align={footerAlign}
    />
  );

  return (
    <AntdModal
      open={open}
      onCancel={onClose}
      centered
      footer={null}
      closable={false}
      title={null}
      width={width ?? SIZE_WIDTH[size]}
      mask={{ closable: maskClosable }}
      keyboard={closeOnEsc}
      destroyOnHidden
      className={styles.modal}
      classNames={{ container: styles.containerReset }}
    >
      <OverlayChrome
        title={title}
        subtitle={subtitle}
        onClose={onClose}
        footer={footer}
        onSubmit={onSubmit}
        bodyClassName={bodyClassName}
      >
        {children}
      </OverlayChrome>
    </AntdModal>
  );
}
